<template>
  <!-- Вікно живе поза сторінками (монтується в App), тому телепортуємо в body:
       так воно не залежить від overflow і z-index конкретного розділу. -->
  <Teleport to="body">
    <div
      v-if="chat.open"
      ref="windowRef"
      class="chat-window"
      :class="{ 'chat-window--minimized': chat.minimized }"
      :style="windowStyle"
    >
      <!-- Шапка: за неї тягнемо вікно -->
      <div class="chat-window__header" @mousedown="startDrag" @touchstart.passive="startDrag">
        <div class="chat-window__title">
          <span class="chat-window__avatar">
            <span class="material-symbols-outlined">{{ chat.kind === 'personnel' ? 'person' : 'local_shipping' }}</span>
          </span>
          <div class="chat-window__title-text">
            <span class="chat-window__name">{{ chat.driverName }}</span>
            <span class="chat-window__subtitle">{{ t('drivers.chat.viaTelegram') }}</span>
          </div>
        </div>
        <div class="chat-window__actions">
          <button type="button" class="chat-window__btn" :title="t('drivers.chat.minimize')" @click.stop="chat.minimized = !chat.minimized">
            <span class="material-symbols-outlined">{{ chat.minimized ? 'expand_less' : 'expand_more' }}</span>
          </button>
          <button type="button" class="chat-window__btn" :title="t('common.close')" @click.stop="closeChat">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
      </div>

      <template v-if="!chat.minimized">
        <div ref="listRef" class="chat-window__messages">
          <div v-if="chat.loading" class="chat-window__hint">{{ t('common.loading') }}</div>
          <div v-else-if="!chat.messages.length" class="chat-window__hint">{{ t('drivers.chat.empty') }}</div>

          <div
            v-for="msg in chat.messages"
            :key="msg.id"
            class="chat-msg"
            :class="[
              msg.direction === 'out' ? 'chat-msg--out' : 'chat-msg--in',
              msg.kind === 'system' ? 'chat-msg--system' : '',
            ]"
          >
            <div class="chat-msg__bubble">
              <!-- Службові повідомлення (інструкція, сповіщення) підписуємо окремо,
                   щоб їх не плутали з реплікою диспетчера -->
              <span v-if="msg.kind === 'system'" class="chat-msg__sender chat-msg__sender--system">
                {{ t('drivers.chat.systemMessage') }}
              </span>
              <span v-else-if="msg.direction === 'out' && msg.senderName" class="chat-msg__sender">{{ msg.senderName }}</span>
              <span class="chat-msg__text">{{ msg.message }}</span>
              <span class="chat-msg__time">{{ formatTime(msg.createdAt) }}</span>
            </div>
          </div>
        </div>

        <div v-if="!chat.telegramLinked" class="chat-window__warning">
          {{ t('drivers.chat.notLinked') }}
        </div>
        <form v-else class="chat-window__composer" @submit.prevent="handleSend">
          <textarea
            ref="inputRef"
            v-model="draft"
            class="chat-window__input"
            :placeholder="t('drivers.chat.placeholder')"
            :title="t('drivers.chat.newlineHint')"
            rows="1"
            :disabled="chat.sending"
            @keydown.enter.exact.prevent="handleSend"
            @input="autoGrow"
          ></textarea>
          <button type="submit" class="chat-window__send" :disabled="!draft.trim() || chat.sending">
            <span class="material-symbols-outlined">send</span>
          </button>
        </form>

        <div v-if="chat.error || chat.loadError" class="chat-window__error">{{ errorText }}</div>

        <!-- Кут для зміни розміру -->
        <div class="chat-window__resize" @mousedown.stop="startResize" @touchstart.passive.stop="startResize"></div>
      </template>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { t, getLanguage } from '../services/i18nService'
import { useDriverChat } from '../composables/useDriverChat'

const { chat, closeChat, sendMessage } = useDriverChat()

const windowRef = ref(null)
const listRef = ref(null)
const inputRef = ref(null)
const draft = ref('')

const MIN_W = 300
const MIN_H = 320

// Позицію і розмір тримаємо в localStorage: вікно має відкриватись там же,
// де користувач його залишив.
const stored = (() => {
  try {
    return JSON.parse(localStorage.getItem('driverChat.geometry') || 'null') || {}
  } catch (e) {
    return {}
  }
})()

const geometry = ref({
  left: Number.isFinite(stored.left) ? stored.left : Math.max(16, window.innerWidth - 400),
  top: Number.isFinite(stored.top) ? stored.top : Math.max(16, window.innerHeight - 560),
  width: Number.isFinite(stored.width) ? stored.width : 370,
  height: Number.isFinite(stored.height) ? stored.height : 520,
})

const windowStyle = computed(() => ({
  left: geometry.value.left + 'px',
  top: geometry.value.top + 'px',
  width: geometry.value.width + 'px',
  height: chat.minimized ? 'auto' : geometry.value.height + 'px',
}))

const persist = () => {
  try {
    localStorage.setItem('driverChat.geometry', JSON.stringify(geometry.value))
  } catch (e) {
    // Приватний режим — просто не запам'ятовуємо геометрію.
  }
}

// --- Перетягування ---
let dragFrom = null
const pointerOf = (e) => (e.touches?.[0] ? e.touches[0] : e)

const startDrag = (e) => {
  const p = pointerOf(e)
  dragFrom = { x: p.clientX, y: p.clientY, left: geometry.value.left, top: geometry.value.top }
  window.addEventListener('mousemove', onDrag)
  window.addEventListener('mouseup', stopDrag)
  window.addEventListener('touchmove', onDrag, { passive: false })
  window.addEventListener('touchend', stopDrag)
}

const onDrag = (e) => {
  if (!dragFrom) return
  if (e.cancelable) e.preventDefault()
  const p = pointerOf(e)
  // Не даємо затягнути вікно за межі екрана — інакше його не повернути.
  const maxLeft = window.innerWidth - 120
  const maxTop = window.innerHeight - 60
  geometry.value.left = Math.min(maxLeft, Math.max(-geometry.value.width + 120, dragFrom.left + (p.clientX - dragFrom.x)))
  geometry.value.top = Math.min(maxTop, Math.max(0, dragFrom.top + (p.clientY - dragFrom.y)))
}

const stopDrag = () => {
  dragFrom = null
  window.removeEventListener('mousemove', onDrag)
  window.removeEventListener('mouseup', stopDrag)
  window.removeEventListener('touchmove', onDrag)
  window.removeEventListener('touchend', stopDrag)
  persist()
}

// --- Зміна розміру ---
let resizeFrom = null

const startResize = (e) => {
  const p = pointerOf(e)
  resizeFrom = { x: p.clientX, y: p.clientY, width: geometry.value.width, height: geometry.value.height }
  window.addEventListener('mousemove', onResize)
  window.addEventListener('mouseup', stopResize)
  window.addEventListener('touchmove', onResize, { passive: false })
  window.addEventListener('touchend', stopResize)
}

const onResize = (e) => {
  if (!resizeFrom) return
  if (e.cancelable) e.preventDefault()
  const p = pointerOf(e)
  geometry.value.width = Math.max(MIN_W, resizeFrom.width + (p.clientX - resizeFrom.x))
  geometry.value.height = Math.max(MIN_H, resizeFrom.height + (p.clientY - resizeFrom.y))
}

const stopResize = () => {
  resizeFrom = null
  window.removeEventListener('mousemove', onResize)
  window.removeEventListener('mouseup', stopResize)
  window.removeEventListener('touchmove', onResize)
  window.removeEventListener('touchend', stopResize)
  persist()
}

onBeforeUnmount(() => {
  stopDrag()
  stopResize()
})

// --- Повідомлення ---
const scrollToBottom = () => {
  nextTick(() => {
    if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight
  })
}

watch(() => chat.messages.length, scrollToBottom)
watch(() => chat.open, (isOpen) => {
  if (isOpen) {
    draft.value = ''
    scrollToBottom()
    nextTick(() => inputRef.value?.focus())
  }
})

const autoGrow = () => {
  const el = inputRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(120, el.scrollHeight) + 'px'
}

const handleSend = async () => {
  const text = draft.value.trim()
  if (!text) return
  const ok = await sendMessage(text)
  if (ok) {
    draft.value = ''
    autoGrow()
    scrollToBottom()
  }
}

const errorText = computed(() => {
  // Помилка надсилання і помилка завантаження історії — різні повідомлення:
  // інакше при збої історії користувач бачить «не вдалося надіслати», нічого не надсилаючи.
  if (chat.error) {
    const known = ['driver_telegram_not_linked', 'telegram_send_failed', 'chat_not_allowed']
    return known.includes(chat.error) ? t(`drivers.chat.errors.${chat.error}`) : t('drivers.chat.errors.generic')
  }
  if (chat.loadError) {
    return chat.loadError === 'chat_not_allowed'
      ? t('drivers.chat.errors.chat_not_allowed')
      : t('drivers.chat.errors.loadFailed')
  }
  return ''
})

// Час приходить у UTC; голу строку без зони браузер вважав би локальною.
const formatTime = (value) => {
  if (!value) return ''
  const date = (String(value).endsWith('Z') || String(value).includes('+'))
    ? new Date(value)
    : new Date(String(value).replace(' ', 'T') + 'Z')
  if (Number.isNaN(date.valueOf())) return ''
  const lang = getLanguage()
  const locale = lang === 'uk' ? 'uk-UA' : lang === 'ru' ? 'ru-RU' : 'en-US'
  return date.toLocaleString(locale, { hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
.chat-window {
  position: fixed;
  z-index: 12000;
  display: flex;
  flex-direction: column;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.chat-window--minimized {
  height: auto !important;
}

.chat-window__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem;
  background: var(--primary);
  color: #fff;
  cursor: move;
  user-select: none;
  flex-shrink: 0;
}

.chat-window__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.chat-window__avatar {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  flex-shrink: 0;
}

.chat-window__avatar .material-symbols-outlined { font-size: 19px; }

.chat-window__title-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chat-window__name {
  font-size: 0.88rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-window__subtitle {
  font-size: 0.7rem;
  opacity: 0.85;
}

.chat-window__actions {
  display: flex;
  gap: 0.15rem;
  flex-shrink: 0;
}

.chat-window__btn {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.chat-window__btn:hover { background: rgba(255, 255, 255, 0.18); }
.chat-window__btn .material-symbols-outlined { font-size: 18px; }

.chat-window__messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  background: var(--bg-secondary);
}

.chat-window__hint {
  margin: auto;
  font-size: 0.82rem;
  color: var(--text-muted);
}

.chat-msg {
  display: flex;
  max-width: 85%;
}

.chat-msg--in { align-self: flex-start; }
.chat-msg--out { align-self: flex-end; }

.chat-msg__bubble {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.35rem 0.55rem;
  border-radius: 12px;
  font-size: 0.84rem;
  line-height: 1.35;
  word-break: break-word;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.08);
}

.chat-msg--in .chat-msg__bubble {
  background: var(--bg-card);
  color: var(--text-primary);
  border-bottom-left-radius: 4px;
}

.chat-msg--out .chat-msg__bubble {
  background: var(--primary-light);
  color: var(--text-primary);
  border-bottom-right-radius: 4px;
}

/* Переноси рядків користувач вводить свідомо (Shift+Enter) і вони доходять до
   Telegram — у вікні їх теж треба зберігати, інакше текст злипається в рядок. */
.chat-msg__text {
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-msg__sender {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--primary);
}

/* Службові повідомлення від системи — приглушені, щоб було видно, що це не
   репліка диспетчера */
.chat-msg--system .chat-msg__bubble {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.chat-msg__sender--system {
  color: var(--text-muted);
  font-weight: 500;
}

.chat-msg__time {
  align-self: flex-end;
  font-size: 0.65rem;
  color: var(--text-muted);
}

.chat-window__composer {
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
  padding: 0.45rem;
  border-top: 1px solid var(--border-light);
  background: var(--bg-card);
  flex-shrink: 0;
}

.chat-window__input {
  flex: 1;
  resize: none;
  border: 1px solid var(--border-default);
  border-radius: 16px;
  padding: 0.4rem 0.7rem;
  font: inherit;
  font-size: 0.85rem;
  line-height: 1.35;
  max-height: 120px;
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.chat-window__input:focus {
  outline: none;
  border-color: var(--primary);
}

.chat-window__send {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
}

.chat-window__send:disabled {
  opacity: 0.5;
  cursor: default;
}

.chat-window__warning,
.chat-window__error {
  padding: 0.5rem 0.6rem;
  font-size: 0.78rem;
  flex-shrink: 0;
}

.chat-window__warning {
  background: var(--warning-light);
  color: var(--warning-text);
}

.chat-window__error {
  background: var(--danger-light);
  color: var(--danger-text);
}

/* Кут для зміни розміру */
.chat-window__resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 50%, var(--border-strong) 50%);
}

@media (max-width: 640px) {
  /* На телефоні плаваюче вікно незручне — розтягуємо на весь екран */
  .chat-window {
    left: 0 !important;
    top: auto !important;
    bottom: 0;
    width: 100% !important;
    height: 75vh !important;
    border-radius: 12px 12px 0 0;
  }
  .chat-window__resize { display: none; }
}
</style>
